import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  formatCurrency,
  eurosToCents,
  centsToEuros,
  RENOVATION_CATEGORY_LABELS
} from '@/lib/utils';
import type { RenovationCategory, RenovationCostItem } from '@/types';

interface RenovationCostsInputProps {
  totalValue: number | null;
  onTotalChange: (value: number | null) => void;
  items: RenovationCostItem[];
  onItemsChange: (items: RenovationCostItem[]) => void;
}

export function RenovationCostsInput({
  totalValue,
  onTotalChange,
  items,
  onItemsChange,
}: RenovationCostsInputProps) {
  const [showDetails, setShowDetails] = useState(items.length > 0);
  const [newItem, setNewItem] = useState<{
    category: RenovationCategory;
    description: string;
    amount: string;
  }>({
    category: 'sonstiges',
    description: '',
    amount: '',
  });

  const calculatedTotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const hasItems = items.length > 0;
  const displayTotal = hasItems ? calculatedTotal : totalValue;

  const handleAddItem = () => {
    if (!newItem.amount) return;

    const item: RenovationCostItem = {
      id: crypto.randomUUID(),
      property_id: '',
      category: newItem.category,
      description: newItem.description || null,
      amount: eurosToCents(parseFloat(newItem.amount)) || 0,
      planned_amount: null,
      is_paid: false,
      paid_date: null,
      contractor: null,
      invoice_number: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    onItemsChange([...items, item]);
    setNewItem({ category: 'sonstiges', description: '', amount: '' });
  };

  const handleRemoveItem = (id: string) => {
    onItemsChange(items.filter(item => item.id !== id));
  };

  const handleTotalChange = (euros: number) => {
    onTotalChange(eurosToCents(euros));
  };

  // Nach Kategorie gruppieren
  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, RenovationCostItem[]>);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Sanierungskosten</Label>
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground transition-colors"
        >
          {showDetails ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Weniger
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Details
            </>
          )}
        </button>
      </div>

      {/* Summen-Eingabe */}
      {!showDetails && (
        <div className="relative">
          <Input
            type="number"
            step="1000"
            value={centsToEuros(displayTotal) || ''}
            onChange={(e) => handleTotalChange(parseFloat(e.target.value) || 0)}
            placeholder="0"
            disabled={hasItems}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted text-sm">
            €
          </span>
        </div>
      )}

      {/* Detail-Ansicht */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-2 space-y-3">
              {/* Summe */}
              {hasItems && (
                <div className="flex justify-between items-center py-2 px-3 bg-background-subtle rounded-md">
                  <span className="text-sm text-foreground-muted">Summe</span>
                  <span className="font-medium">{formatCurrency(calculatedTotal)}</span>
                </div>
              )}

              {/* Gruppierte Items */}
              {Object.entries(groupedItems).map(([category, categoryItems]) => (
                <div key={category} className="space-y-2">
                  <p className="text-xs font-medium text-foreground-muted">
                    {RENOVATION_CATEGORY_LABELS[category as RenovationCategory]}
                  </p>
                  {categoryItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between py-2 px-3 bg-background-subtle rounded-md group"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">
                          {item.description || RENOVATION_CATEGORY_LABELS[item.category]}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{formatCurrency(item.amount)}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-background-elevated rounded transition-opacity"
                        >
                          <Trash2 className="w-3 h-3 text-negative" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* Neue Position hinzufügen */}
              <Card className="p-3 space-y-3">
                <p className="text-xs font-medium text-foreground-muted">Position hinzufügen</p>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value as RenovationCategory })}
                    className="input h-9 text-sm bg-background border border-border rounded-md px-3"
                  >
                    {Object.entries(RENOVATION_CATEGORY_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <Input
                    type="number"
                    step="100"
                    value={newItem.amount}
                    onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
                    placeholder="Betrag €"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Beschreibung (optional)"
                    className="h-9 text-sm flex-1"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleAddItem}
                    disabled={!newItem.amount}
                    className="h-9"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
